'use strict';
import get from 'lodash/get';
import noop from 'lodash/noop';
import {createEventHandler} from 'recompose';
import {Observable} from 'rxjs/Observable';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {stateGoBack} from '../../app/actions';
import {getAsset, getProject} from '../../app/model/reducers/state';
import {toStringStatic} from '../../assets/domain/AssetDomain';
import {IONIC_VIEW_ENTER, IONIC_VIEW_LEAVE, SCOPE_DESTROY} from '../../config/constants/view-events';
import {supportsDownload} from '../../core-utilities-implementation/device-services/DeviceServicesCordova';
import {getAsString, getCollection, getDomainLink, getId, getLink} from '../../core-utilities/domain/ReduxUtils';
import {eventToObservable, observeNewOnScope} from '../../core-utilities/rx-utils';
import {isLockedStatus} from '../../projects/services/ParentStatusService';
import {TaskType} from '../../tasks/enums/TaskType';
import {getAssetStatusPermission, getStatusPermission} from '../domain/AbstractRequirement';
import {canAddAssetFile, canAddFile} from '../domain/files/AbstractFileRequirement';
import {getRequirementFileName} from '../utils';


RequirementsFilesDetailsController.$inject = [
  '$scope',
  '$miscUtils',
  '$contextMenu',
  '$permissions',
  'rxPermissionsService',
  '$popup',
  '$timeout',
  '$ngRedux',
  '$ionicViewSwitcher',
  'FileRequirementEditorFactory',
  'filePreviewService',
  'FilesDownloadService',
  '$ionicSideMenuDelegate',
  '$persistentCacheFactory',
  'FilePickerFactory',
  'PersistentUploaderFactory',
  'DeviceServices',
  'childModelDeletedManager',
  'AssetFileRequirementDomain',
  'FileDomain',
  '$toast',
  '$download',
  'FILE_USAGE',
  'requirementsFilesHelper',
  'requirement',
  'ctrlConfig',
];

/* var VIEW_ACTIONS = {
 canChangeStatus: '(asset_)requirement_(approval|submission)_edit',
 canAddFile: '(asset_)requirement_(approval|submission)_edit',
 canDownloadFiles: '(asset_)task_fulfillments_view',
 canEdit: '(template_|asset_)requirement_data_edit',
 canDelete: '(template_|asset_)requirement_data_delete',
 canApprove: '(asset_)requirement_approval_edit'
 };*/

function get_VIEW_ACTIONS_PROJECT(roleData, reqStatus) {
  const isLocked = roleData.projectIsLocked || roleData.taskIsLocked || isLockedStatus(reqStatus);

  return {
    canChangeStatus: getStatusPermission(reqStatus),
    canAddFile: isLocked ? canAddFile(reqStatus) + '_locked' : canAddFile(reqStatus),
    canDownloadFiles: 'task_fulfillments_view',
    canEdit: 'requirement_data_edit',
    canDelete: isLocked ? 'requirement_data_delete_locked' : 'requirement_data_delete',
    canApprove: 'requirement_approval_edit',
  };
}

function get_VIEW_ACTIONS_ASSET(roleData, reqStatus) {
  const isLocked = roleData.projectIsLocked
    || roleData.assetProjectIsLocked
    || roleData.assetTaskIsLocked
    || isLockedStatus(reqStatus);

  return {
    canChangeStatus: getAssetStatusPermission(reqStatus),
    canAddFile: isLocked ? canAddAssetFile(reqStatus) + '_locked' : canAddAssetFile(reqStatus),
    canDownloadFiles: 'asset_task_fulfillments_view',
    canEdit: 'asset_requirement_data_edit',
    canDelete: isLocked ? 'asset_requirement_data_delete_locked' : 'asset_requirement_data_delete',
    canApprove: 'asset_requirement_approval_edit',
  };
}

export function RequirementsFilesDetailsController(
  $scope,
  $miscUtils,
  $contextMenu,
  $permissions,
  rxPermissionsService,
  $popup,
  $timeout,
  $ngRedux,
  $ionicViewSwitcher,
  FileRequirementEditorFactory,
  filePreviewService,
  FilesDownloadService,
  $ionicSideMenuDelegate,
  $persistentCacheFactory,
  FilePickerFactory,
  PersistentUploaderFactory,
  DeviceServices,
  childModelDeletedManager,
  AssetFileRequirementDomain,
  FileDomain,
  $toast,
  $download,
  FILE_USAGE,
  requirementsFilesHelper,
  requirement,
  ctrlConfig,
) {

  const leave$ = eventToObservable($scope, SCOPE_DESTROY);
  const vm = $scope.requirementDetails = this;
  const openingFileIdSubject = new ReplaySubject();
  const openingFileId$ = openingFileIdSubject
    .asObservable()
    .startWith(null);
  // eslint-disable-next-line no-var prefer-const
  let uploader;
  // let filesAsArray = []; // stores all valid preview files. currently just images
  vm.taskType = ctrlConfig.taskType;
  vm.requirement = requirement;
  vm.editName = getCollection(vm.requirement) !== AssetFileRequirementDomain.COLLECTION
    || !getLink(vm.requirement, 'requirement').id;

  // vm.filePicker; // assigned in configureUploading

  /* Controller Actions */
  vm.onFileSelect = onFileSelect; // used for drag and drop upload
  vm.openFilePicker = openFilePicker;
  vm.downloadAllFiles = downloadAllFiles;
  vm.placeholderAction = placeholderAction;
  vm.showQuickApprove = showQuickApprove;
  vm.quickApproveConfirm = quickApproveConfirm;
  vm.deleteRequirement = deleteRequirement;

  /* Presentation Logic */
  vm.showHeaderAction = showHeaderAction;
  vm.getStatusPermission = requirement.$class.getStatusPermission;

  $scope.$on(IONIC_VIEW_ENTER, function () {
    $ionicSideMenuDelegate.$getByHandle('main_menu').canDragContent(false);
  });

  $scope.$on(IONIC_VIEW_LEAVE, function () {
    filePreviewService.cancel();
    $ionicSideMenuDelegate.$getByHandle('main_menu').canDragContent(true);
  });

  $scope.$on(SCOPE_DESTROY, function () {
    filePreviewService.cancel();
    $ionicSideMenuDelegate.$getByHandle('main_menu').canDragContent(true);
  });

  childModelDeletedManager.watchModel(requirement, goBack, $scope);
  // eslint-disable-next-line prefer-const
  uploader = configureUploading(); // initializes vm.filePicker
  initHeaderAction(); // requires "configureUploading" to be performed first. Depends on vm.filePicker
  $timeout(loadFiles, 125); // delayed to minimize performance impact to the initial view load of reqs with several files

  bindReactProps();

  function bindReactProps() {
    const {
      handler: handleItemContextMenu,
      stream: itemContextMenu$,
    } = createEventHandler();

    const onItemContextMenu = (e, model) => handleItemContextMenu(model);

    const BASE_PROPS = {
      onDropFile: onFileSelect,
      onDropFile_wrap: false,

      onClickOptions: showMenu,
      onClickOptions_wrap: false,
      onItemContextMenu: onItemContextMenu,
      onItemContextMenu_wrap: false,

      onOpenPreviewer: openPreviewer,
      onOpenPreviewer_wrap: false,
      onOpenCamera: DeviceServices.supportsCamera() ?
        vm.filePicker.openCamera.bind(vm.filePicker, null, null) : noop,
      onOpenCamera_wrap: false,
      onOpenVideo: DeviceServices.supportsVideo() ?
        vm.filePicker.openVideo.bind(vm.filePicker, null, null) : noop,
      onOpenVideo_wrap: false,
      onOpenFilePicker: openFilePicker,
      onOpenFilePicker_wrap: false,
      onOpenIOSFilePicker: DeviceServices.supportsPickFile() ?
        vm.filePicker.uploadMobileIOSFile.bind(vm.filePicker, null, null) : noop,
      onOpenIOSFilePicker_wrap: false,
      onDisabledAttempt: disabledAttempt,
      onDownloadAllFiles: downloadAllFiles,
      onDownloadAllFiles_wrap: false,
      onDeleteRequirement: deleteRequirement,
      onDeleteRequirement_wrap: false,
      onQuickApproveConfirm: quickApproveConfirm,
      onQuickApproveChange: onQuickApproveChange,
    };

    vm.permissions = {};
    vm.viewProps = {
      ...BASE_PROPS,
    };

    const permissions$ = observePermissions();

    const props$ = Observable
      .combineLatest(
        requirement.$observe(),
        permissions$,
        observeNewOnScope($scope, showQuickApprove)
          .distinctUntilChanged(),
        openingFileId$,
        nextState,
      )
      .takeUntil(leave$)
      .share();

    itemContextMenu$
      .withLatestFrom(props$, permissions$)
      .takeUntil(leave$)
      .subscribe(([model, props, permissionsData]) => {
        requirementsFilesHelper.showFileMenu({
          taskType: ctrlConfig.taskType,
          requirement: props.model,
          roleData: permissionsData.roleData,
          canAddFile: props.canAddFile,
        }, model);
      });

    props$.subscribe(nextState => $scope.$safeApply(() => vm.viewProps = nextState));

    function nextState(
      model,
      permissionsData,
      showQuickApprove,
      openingFileId,
    ) {
      return {
        ...BASE_PROPS,

        model: serialize(model),
        openingFileId,
        canAddFile: get(permissionsData.permissions, 'canAddFile', false),
        canDownloadFiles: get(permissionsData.permissions, 'canDownloadFiles', false),
        canEdit: get(permissionsData.permissions, 'canEdit', false),
        canDelete: get(permissionsData.permissions, 'canDelete', false),
        showQuickApprove,
      };
    }

    function serialize(model) {
      return {
        id: getId(model),
        collection: getCollection(model),
        ...model.$serialize(true),
      };
    }
  }

  function onQuickApproveChange(isWorking) {
    vm.isApproving = isWorking;
  }

  /*****************************************************************************************************************
   * Header Buttons
   ****************************************************************************************************************/

  // go back to
  function goBack() {
    if (!vm.task) {
      $ngRedux.dispatch(stateGoBack({state: ctrlConfig.taskListState}));
    } else {
      $ngRedux.dispatch(stateGoBack({state: ctrlConfig.taskDetailsState, params: {taskId: getId(vm.task)}}));
    }
  }

  function initHeaderAction() {
    if (DeviceServices.supportsCamera()) {
      vm.headerActionIcon = 'ion-ios-camera-outline';
      vm.headerAction = vm.filePicker.openCamera.bind(vm.filePicker, null, null);
    }
    if (DeviceServices.supportsVideo()) {
      vm.headerActionIcon = 'ion-ios-videocam-outline';
      vm.headerAction = vm.filePicker.openVideo.bind(vm.filePicker, null, null);
    } else {
      vm.headerActionIcon = 'ion-android-upload';
      vm.headerAction = openFilePicker.bind(null);
    }
  }

  /*****************************************************************************************************************
   * Context Menu Actions
   ****************************************************************************************************************/

  const RETRY_UPLOADS = {
    permission: null,
    action: {messageId: 'requirements.files.context.retyuploads.label', action: uploader.retryUploads},
  };

  const DOWNLOAD_ALL_REQ = {
    permission: $permissions.permissionPrefix(ctrlConfig.taskType) + 'task_fulfillments_view',
    action: {messageId: 'requirements.files.context.download.files.label', action: downloadAllFiles},
  };

  const DELETE_REQ = {
    permission: $permissions.permissionPrefix(ctrlConfig.taskType) + 'requirement_data_delete',
    action: {messageId: 'fulfillments.context.delete.label', action: deleteRequirement, important: true},
  };

  let menuOptions;

  function showMenu() {
    if (!menuOptions) return;

    $contextMenu.showByPermission(menuOptions);
  }

  function buildMenu(task) {
    const taskId = getId(task);
    if (!taskId) return;

    const checkActions = [];
    const addAction = ctrlConfig.taskType === TaskType.ASSET
      ? $contextMenu.addActionByAssetTask
      : $contextMenu.addActionByTask;

    if (DeviceServices.supportsCamera() || DeviceServices.supportsVideo())
      $contextMenu.addAction(checkActions, RETRY_UPLOADS);

    if (supportsDownload())
      addAction(checkActions, taskId, DOWNLOAD_ALL_REQ);

    addAction(checkActions, taskId, DELETE_REQ);

    return {
      titleMessageId: 'requirements.files.context.title.label',
      cancelMessageId: 'defaults.context.cancel.label',
      actions: checkActions,
    };
  }

  function downloadAllFiles() {
    if (!vm.requirement) return;
    FilesDownloadService.downloadRequirementFiles(getId(vm.requirement), vm.requirement.name, ctrlConfig.taskType);
  }

  let deleteRequirementCalled = false;

  function deleteRequirement() {
    if (deleteRequirementCalled || !vm.requirement) return;
    deleteRequirementCalled = true;

    // TODO(i18n): translate task delete confirmation
    return $popup.confirm({
      title: 'Delete Requirement',
      template: 'Are you sure you want to delete ' + vm.requirement.toString(),
    })
      .then(function (result) {
        if (result) {
          return vm.requirement.$delete()
            .then(function () {
              vm.requirement = null;
              goBack();
            });
        }
      })
      .finally(__deleteRequirementFinally);
  }

  function __deleteRequirementFinally() {
    deleteRequirementCalled = false;
  }


  /*****************************************************************************************************************
   * Controller Actions
   ****************************************************************************************************************/

  // TODO: update to get the files from redux
  function openPreviewer(file, files) {
    try {
      const name = getRequirementFileName(requirement, file);
      openingFileIdSubject.next(file.id || file.key);

      return filePreviewService.openPreviewer(file, {files, name})
        .then(() => {
          openingFileIdSubject.next(null);
        })
        .catch(e => {
          openingFileIdSubject.next(null);
          if (e) {
            console.error('Failed to open previewer', e);
          }
        });
    } catch (e) {
      openingFileIdSubject.next(null);
    }
  }

  function showQuickApprove() {
    if (!vm.permissions.canApprove) {
      return false;
    } else {
      return vm.isApproving
        || (vm.requirement.status !== 'approved' && vm.requirement.status !== 'cancelled');
    }
  }

  function quickApproveConfirm() {
    return vm.requirement.updateStatus('approved');
  }

  /*****************************************************************************************************************
   * Uploading Configuration
   ****************************************************************************************************************/

  function configureUploading() {
    // TODO: may need to harden against race condition, loading the current fulfillment
    // TODO(requirements): update to new model
    const uploader = PersistentUploaderFactory({
      /* File upload options*/
      getModelId: getModelId,
      getFilename: getNewFileName,
      usage: FILE_USAGE.GALLERY,
      generateFileRequest: vm.requirement.$generateFileRequest.bind(vm.requirement),

      onSuccess: 'get("$domain").getDomain(collection).$addFileDomain(id, fileDomain)',
      getLocals: () => ({
        id: getId(vm.requirement),
        collection: getCollection(vm.requirement),
      }),
    });

    uploader.bindUploadsToScope($scope, {
      pendingUploadsName: 'uploading',
    });

    vm.filePicker = FilePickerFactory($scope, {
      getNewFilePath: getNewFilePath,
      getNewFileName: getNewFileName,
      onSuccess: DeviceServices.isWebView() ? uploader.onSuccess : onFileSelect,
      onError: uploader.onError,
    });

    return uploader;
  }

  function openFilePicker() {
    disabledAttempt();

    vm.filePicker.pickFile(true, true, {
      filename: vm.requirement.toString(),
      getPath: getNewFulfillmentPath,
      usage: FILE_USAGE.GALLERY,
      onSuccess: 'get("$domain").getDomain(collection).$addFileDomain(id, fileDomain)',
      onSuccessLocals: {
        id: getId(vm.requirement),
        collection: getCollection(vm.requirement),
      },
    });

    function getNewFulfillmentPath() {
      const options = requirement.$generateFileRequest();

      return {
        path: options.path,
        firebasePath: options.firebasePath,
      };
    }
  }

  // TODO: need to make this configurable to also work with projects
  function getNewFilePath() {
    const rootState = $ngRedux.getState();
    switch (ctrlConfig.taskType) {
      case TaskType.ASSET:
        return getAsString(getAsset(rootState), toStringStatic);

      case TaskType.PROJECT:
      case TaskType.TEMPLATE:
        return getAsString(getProject(rootState));
    }
  }

  function getNewFileName() {
    return vm.requirement.toString();
  }

  function getModelId() {
    return getId(vm.requirement);
  }

  function placeholderAction() {
    DeviceServices.supportsCamera() ? vm.filePicker.openCamera() : openFilePicker();
  }

  /* TODO(requirements): revert to simple loading */
  function loadFiles() {
    const taskProperty = ctrlConfig.taskType === TaskType.ASSET ? 'assetTask' : 'task';

    const reqId$ = observeNewOnScope($scope, getModelId)
      .takeUntil(leave$)
      .filter(id => !!id)
      .take(1)
      .share();

    // get task
    reqId$.switchMap(() => vm.requirement[taskProperty]
      ? Observable.of(vm.requirement[taskProperty])
      : getDomainLink(vm.requirement, taskProperty))
      .filter(task => task && getLink(vm.requirement, taskProperty).id === getId(task))
      .take(1)
      .subscribe(task => {
        vm.task = task;
        menuOptions = buildMenu(task);

        // TODO(RxJS): this is still technically subscription nesting. fix it
        childModelDeletedManager.watchModel(vm.task, onTaskDeleted, $scope);
      });
  }

  function onTaskDeleted() {
    vm.task = null; // forces goBack to send user to task list, instead of task details
    goBack();
  }

  function onFileSelect(files) {
    // console.log('RequirementDetails.onFileSelect', files);
    if (!(files && files.length)) return;

    if (vm.permissions.canAddFile === false) {
      return $toast.showI18nMessage({
        messageId: 'requirements.files.upload.denied.message',
        messageParams: {name: vm.requirement.toString()},
        type: $toast.TOAST_TYPES.error,
      });
    }

    if (vm.requirement.status === 'approved' || vm.requirement.status === 'cancelled')  {
      return $toast.showI18nMessage({
        messageId: 'requirements.files.upload.denied.status.message',
        type: $toast.TOAST_TYPES.error,
      });
    } if (vm.task.status === 'approved' || vm.task.status === 'cancelled')  {
      return $toast.showI18nMessage({
        messageId: 'tasks.files.upload.denied.status.message',
        type: $toast.TOAST_TYPES.error,
      });
    }

    uploader.onSuccess({
      files: files,
    });
  }

  function disabledAttempt() {
    if (vm.requirement.status === 'approved' || vm.requirement.status === 'cancelled')  {
      return $toast.showI18nMessage({
        messageId: 'requirements.files.upload.denied.status.message',
        type: $toast.TOAST_TYPES.error,
      });
    } if (vm.task.status === 'approved' || vm.task.status === 'cancelled')  {
      return $toast.showI18nMessage({
        messageId: 'tasks.files.upload.denied.status.message',
        type: $toast.TOAST_TYPES.error,
      });
    }
  }


  /*****************************************************************************************************************
   * Presentation Logic
   ****************************************************************************************************************/

  vm.viewConfig = {
    header: 'requirements.files.header.label',
    info: {
      show: 'requirements.files.details.show.label',
      hide: 'requirements.files.details.hide.label',
    },
    details: {
      name: 'requirements.files.details.name.label',
      description: 'requirements.files.details.description.label',
      count: 'requirements.files.details.count.label',
      approximate: 'requirements.files.details.approximate.label',
    },
    domainClass: (ctrlConfig.taskType === TaskType.ASSET ? 'Asset' : '') + 'FileRequirementDomain',
  };

  function observePermissions() {
    if (ctrlConfig.taskType === TaskType.ASSET) {
      return Observable.fromPromise(getDomainLink(requirement, 'assetTask'))
        .switchMap(task => {
          const parentTaskId = getLink(task, 'task').id;
          const parentTask$ = parentTaskId ? Observable.fromPromise(getDomainLink(task, 'task')) : null;
          return Observable
            .combineLatest(
              rxPermissionsService.buildUserAssetTaskRoleData$(task.$observe(), parentTask$),
              vm.requirement.$observe()
                .map(requirement => requirement ? requirement.status : null),
              (roleData, reqStatus) => {
                return rxPermissionsService.buildAssetTaskState(roleData, getVIEW_ACTIONS(roleData, reqStatus));
              })
            .startWith({permissions: {}, roleData: {}});
        });
    } else {
      return Observable.fromPromise(getDomainLink(requirement, 'task'))
        .switchMap(task =>
          Observable
            .combineLatest(
              rxPermissionsService.buildUserTaskRoleData$(task.$observe()),
              vm.requirement.$observe()
                .map(requirement => requirement ? requirement.status : null),
              (roleData, reqStatus) => {
                return rxPermissionsService.buildTaskState(roleData, getVIEW_ACTIONS(roleData, reqStatus));
              })
            .startWith({permissions: {}, roleData: {}})
        );
    }
  }

  function getVIEW_ACTIONS(roleData, reqStatus) {
    switch (ctrlConfig.taskType) {
      case TaskType.ASSET:
        return get_VIEW_ACTIONS_ASSET(roleData, reqStatus);

      case TaskType.PROJECT:
        return get_VIEW_ACTIONS_PROJECT(roleData, reqStatus);
    }
  }

  function showHeaderAction() {
    return vm.permissions.canAddFile;
  }
}



// WEBPACK FOOTER //
// ./src/js/requirements/controllers/RequirementsFilesDetailsController.js